import {
  Box,
  FormControlLabel,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  FormHelperText,
  Autocomplete,
  TextField,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import { FC, useState } from 'react'
import InfoIcon from '@mui/icons-material/Info'
import { UseQueryResult } from 'react-query'
import IOSSwitch from '../../components/IOSSwitch'

export interface AutoAcceptToggleProps {
  connection: UseQueryResult<any, unknown>
  patchConnectionHandler: (options: any) => void
}
interface ConnectionSettings {
  autoAcceptOnPrint: boolean
  orderMode: string[]
  orderType: string[]
}

/**
 * Auto Accept Toggle component
 */
export const AutoAcceptToggle: FC<AutoAcceptToggleProps> = ({
  connection,
  patchConnectionHandler,
}) => {
  const [isAutoAcceptEnabled, setIsAutoAcceptEnabled] = useState(
    connection.data?.autoAcceptOnPrint
  )

  const orderTypeOptions = [
    'DELIVERY',
    'PICKUP',
    'DINE-IN',
    'WAITING',
    'IN_STORE',
  ]
  const orderModeOptions = ['ONLINE', 'POS', 'KIOSK']
  const [orderMode, setOrderMode] = useState(
    connection.data?.orderMode ?? orderModeOptions
  )
  const [orderType, setOrderType] = useState(
    connection.data?.orderType ?? orderTypeOptions
  )

  /**
   * Handle toggling of the auto-accept feature
   */
  const updateConnectionSettings = (updates: Partial<ConnectionSettings>) => {
    if (updates.autoAcceptOnPrint !== undefined) {
      setIsAutoAcceptEnabled(updates.autoAcceptOnPrint)
    }
    if (updates.orderMode !== undefined) {
      setOrderMode(updates.orderMode)
    }
    if (updates.orderType !== undefined) {
      setOrderType(updates.orderType)
    }

    patchConnectionHandler(updates)
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<SettingsIcon />}>
        <Typography variant="h6">Configuration</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} pl={1} alignItems="center">
          <Grid item xs={8}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1">Auto accept orders</Typography>
              <Tooltip title="When enabled, orders will be automatically accepted once printed via Sunmi Cloud Printer.">
                <IconButton sx={{ pt: 0, pb: 0 }}>
                  <InfoIcon fontSize="small" sx={{ p: 0 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={4} container justifyContent="flex-end">
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={isAutoAcceptEnabled}
                  onChange={(e, checked) =>
                    updateConnectionSettings({ autoAcceptOnPrint: checked })
                  }
                />
              }
              label=""
            />
          </Grid>

          <Grid item xs={5}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1">Order mode</Typography>
            </Box>
            <FormHelperText>Select the source of the order</FormHelperText>
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              multiple
              id="order-mode"
              size="small"
              options={orderModeOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              value={orderMode}
              onChange={(e, newValue) =>
                updateConnectionSettings({ orderMode: newValue })
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Order Mode"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={5}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1">Order Type</Typography>
            </Box>
            <FormHelperText>
              Choose the type of orders you want to print
            </FormHelperText>
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              multiple
              id="order-types"
              size="small"
              options={orderTypeOptions}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              value={orderType}
              onChange={(e, newValue) =>
                updateConnectionSettings({ orderType: newValue })
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Order Types"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}
